import { useMemo, useState } from 'react';
import { Button, Card, Table, Space, Form, Input, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ToolOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { trpc } from '@/lib/api';
import { withDevModalFilePath, NumericInput } from '../common';

type TemplateTool = {
  id: string;
  templateId: string;
  toolName: string;
  requiredQuantity: number;
  notes: string | null;
  createdAt: Date;
  updatedAt: Date;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeTemplateTool = (value: unknown): TemplateTool | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    templateId: typeof value.templateId === 'string' ? value.templateId : '',
    toolName: typeof value.toolName === 'string' ? value.toolName : '名称未設定',
    requiredQuantity: typeof value.requiredQuantity === 'number' ? value.requiredQuantity : 1,
    notes: typeof value.notes === 'string' ? value.notes : null,
    createdAt: value.createdAt instanceof Date ? value.createdAt : new Date(0),
    updatedAt: value.updatedAt instanceof Date ? value.updatedAt : new Date(0),
  };
};

const normalizeTemplateToolList = (value: unknown): TemplateTool[] => {
  const candidates =
    Array.isArray(value)
      ? value
      : isRecord(value) && Array.isArray(value.items)
        ? value.items
        : isRecord(value) && Array.isArray(value.data)
          ? value.data
          : [];

  return candidates
    .map((item) => normalizeTemplateTool(item))
    .filter((item): item is TemplateTool => item !== null);
};

interface Props {
  templateId: string;
}

const { Modal } = withDevModalFilePath('client/src/components/work-order/TemplateToolsSection.tsx');

export function TemplateToolsSection({ templateId }: Props) {
  const { message: messageApi } = App.useApp();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<TemplateTool | null>(null);
  const [form] = Form.useForm();

  // データ取得
  const { data: templateToolsData, refetch } = trpc.workOrderTemplate.getTools.useQuery(
    { templateId },
    { enabled: Boolean(templateId) }
  );
  const templateTools = useMemo(
    () => normalizeTemplateToolList(templateToolsData),
    [templateToolsData]
  );

  // Mutations
  const addToolMutation = trpc.workOrderTemplate.addTool.useMutation({
    onSuccess: () => {
      messageApi.success('工具を追加しました');
      setAddModalOpen(false);
      form.resetFields();
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const updateToolMutation = trpc.workOrderTemplate.updateTool.useMutation({
    onSuccess: () => {
      messageApi.success('工具情報を更新しました');
      setEditModalOpen(false);
      form.resetFields();
      setSelectedTool(null);
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const removeToolMutation = trpc.workOrderTemplate.removeTool.useMutation({
    onSuccess: () => {
      messageApi.success('工具を削除しました');
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  // ハンドラー
  const handleAddTool = () => {
    form.resetFields();
    setAddModalOpen(true);
  };

  const handleAddSubmit = async () => {
    try {
      const values = await form.validateFields();
      addToolMutation.mutate({
        templateId,
        toolName: values.toolName,
        requiredQuantity: values.requiredQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleEdit = (record: TemplateTool) => {
    setSelectedTool(record);
    form.setFieldsValue({
      toolName: record.toolName,
      requiredQuantity: record.requiredQuantity,
      notes: record.notes,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedTool) return;

    try {
      const values = await form.validateFields();
      updateToolMutation.mutate({
        templateId,
        toolId: selectedTool.id,
        toolName: values.toolName,
        requiredQuantity: values.requiredQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleDelete = (record: TemplateTool) => {
    Modal.confirm({
      title: '工具を削除しますか？',
      content: `「${record.toolName}」をこのテンプレートから削除します。`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        removeToolMutation.mutate({
          templateId,
          toolId: record.id,
        });
      },
    });
  };

  const columns: ColumnsType<TemplateTool> = [
    {
      title: '工具名',
      dataIndex: 'toolName',
      key: 'toolName',
    },
    {
      title: '必要数量',
      dataIndex: 'requiredQuantity',
      key: 'requiredQuantity',
      width: 120,
      render: (qty) => `${qty}個`,
    },
    {
      title: '備考',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (val) => val || '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <ToolOutlined />
            必要工具
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddTool}
          >
            工具を追加
          </Button>
        }
      >
        <Table<TemplateTool>
          columns={columns}
          dataSource={templateTools}
          rowKey="id"
          pagination={false}
        />
      </Card>

      {/* 工具追加モーダル */}
      <Modal
        title="工具を追加"
        open={addModalOpen}
        onOk={handleAddSubmit}
        onCancel={() => {
          setAddModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={addToolMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="工具名"
            name="toolName"
            rules={[{ required: true, message: '工具名を入力してください' }]}
          >
            <Input placeholder="例: トルクレンチ、デジタルマルチメーター" />
          </Form.Item>

          <Form.Item
            label="必要数量"
            name="requiredQuantity"
            rules={[{ required: true, message: '数量を入力してください' }]}
            initialValue={1}
          >
            <NumericInput min={1} style={{ width: '100%' }} addonAfter="個" />
          </Form.Item>

          <Form.Item label="備考" name="notes">
            <Input.TextArea rows={3} placeholder="備考を入力（任意）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 工具編集モーダル */}
      <Modal
        title="工具情報を編集"
        open={editModalOpen}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalOpen(false);
          form.resetFields();
          setSelectedTool(null);
        }}
        confirmLoading={updateToolMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="工具名"
            name="toolName"
            rules={[{ required: true, message: '工具名を入力してください' }]}
          >
            <Input placeholder="例: トルクレンチ、デジタルマルチメーター" />
          </Form.Item>

          <Form.Item
            label="必要数量"
            name="requiredQuantity"
            rules={[{ required: true, message: '数量を入力してください' }]}
          >
            <NumericInput min={1} style={{ width: '100%' }} addonAfter="個" />
          </Form.Item>

          <Form.Item label="備考" name="notes">
            <Input.TextArea rows={3} placeholder="備考を入力（任意）" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
